"use client";

import { useState } from "react";

export type DeliveryAddress = {
  name: string;
  phone: string;
  line1: string;
  line2?: string;
  city: string;
  postcode: string;
};

export function AddressForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (address: DeliveryAddress) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<DeliveryAddress>({
    name: "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    postcode: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.phone.trim()) e.phone = "Required";
    if (!form.line1.trim()) e.line1 = "Required";
    if (!form.city.trim()) e.city = "Required";
    if (!form.postcode.trim()) e.postcode = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        ...form,
        line2: form.line2?.trim() || undefined,
      });
    }
  }

  function update(field: keyof DeliveryAddress, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  const inputClass = "w-full border border-[var(--color-border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/30 transition-colors";
  const errorInputClass = "border-red-300";
  const labelClass = "block text-xs font-medium text-gray-600 mb-1";

  return (
    <form onSubmit={handleSubmit} className="bg-[var(--color-accent)]/5 border border-[var(--color-accent)]/30 rounded-xl p-4 my-3 space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="font-medium text-sm text-[var(--color-accent)]">Delivery Details</span>
      </div>

      <div>
        <label className={labelClass}>Full Name *</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
          className={`${inputClass} ${errors.name ? errorInputClass : ""}`}
          placeholder="John Smith"
        />
        {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
      </div>

      <div>
        <label className={labelClass}>Phone Number *</label>
        <input
          type="tel"
          value={form.phone}
          onChange={(e) => update("phone", e.target.value)}
          className={`${inputClass} ${errors.phone ? errorInputClass : ""}`}
          placeholder="07123 456789"
        />
        {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
      </div>

      <div>
        <label className={labelClass}>Address Line 1 *</label>
        <input
          type="text"
          value={form.line1}
          onChange={(e) => update("line1", e.target.value)}
          className={`${inputClass} ${errors.line1 ? errorInputClass : ""}`}
          placeholder="123 High Street"
        />
        {errors.line1 && <p className="text-xs text-red-500 mt-1">{errors.line1}</p>}
      </div>

      <div>
        <label className={labelClass}>Address Line 2 (optional)</label>
        <input
          type="text"
          value={form.line2}
          onChange={(e) => update("line2", e.target.value)}
          className={`${inputClass}`}
          placeholder="Flat 2, Building Name"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>City *</label>
          <input
            type="text"
            value={form.city}
            onChange={(e) => update("city", e.target.value)}
            className={`${inputClass} ${errors.city ? errorInputClass : ""}`}
            placeholder="Ashford"
          />
          {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city}</p>}
        </div>
        <div>
          <label className={labelClass}>Postcode *</label>
          <input
            type="text"
            value={form.postcode}
            onChange={(e) => update("postcode", e.target.value.toUpperCase())}
            className={`${inputClass} ${errors.postcode ? errorInputClass : ""}`}
            placeholder="TN24 8AB"
            style={{ textTransform: "uppercase" }}
          />
          {errors.postcode && <p className="text-xs text-red-500 mt-1">{errors.postcode}</p>}
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          className="flex-1 bg-[var(--color-accent)] text-white rounded-lg px-4 py-3 text-sm font-medium hover:bg-[var(--color-accent-light)] transition-colors min-h-[48px] flex items-center justify-center"
        >
          Confirm Address
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-3 text-sm text-gray-500 hover:text-gray-700 transition-colors min-h-[48px] flex items-center justify-center"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
